<!-- 
 do not edit or change the content and sizing of the stamp card.
 If want to change the size of the card change the style: zoom to scale it.
-->

<template>
  <FlexCol
    class="text-white justify-content-between align-items-center position-relative"
    :class="{
      'edit-none': !editMode || editSection === 'none' || !editSection,
    }"
    style="
      aspect-ratio: 1.61/1;
      width: 43rem;
      padding-inline: 1.5rem;
      padding-block: 2rem;
      gap: 1.5rem;
      border-radius: 24px;
    "
    :style="{
      background: cardSettings.backgroundColor,
    }"
  >
    <FlexRow
      class="justify-content-center fw-bold text-center edit-mode-grey"
      :class="{ 'is-editing': editMode && editSection === 'description' }"
      style="
        font-size: 28px;
        --section-height: 85px;
        --edit-border-radius: 24px 24px 0 0;
      "
      :style="{ color: cardSettings.textColor }"
    >
      {{ cardSettings.description }}
    </FlexRow>

    <FlexCol
      style="gap: 1rem; --section-top: 85px"
      class="edit-mode-grey"
      :class="{ 'is-editing': editMode && editSection === 'stamps' }"
      :style="{ '--section-height': totalStamps <= 10 ? '240px' : '357.5px' }"
    >
      <FlexRow
        v-for="(row, rowIndex) in stampRows"
        :key="rowIndex"
        class="justify-content-center"
        :style="{ gap: `${getRowGap(row.length)}` }"
      >
        <Stamp
          v-for="index in row"
          :key="index"
          :settings="{
            index,
            checkedStamps,
            totalStamps,
            textColor: cardSettings.textColor,
            backgroundColor: cardSettings.backgroundColor,
            beforeStampsTextColor: cardSettings.beforeStampsTextColor,
            beforeStampsBackgroundColor:
              cardSettings.beforeStampsBackgroundColor,
            stampIcon: cardSettings.stampIcon,
            stampImageUrl: cardSettings.stampImageUrl,
            reward: reward,
            rewardEmoji: cardSettings.rewardEmoji,
            rewardImageUrl: cardSettings.rewardImageUrl,
            dateText: formatStampDate(index),
            rewardBackgroundColor: cardSettings.rewardBackgroundColor,
            rewardTextColor: cardSettings.rewardTextColor,
          }"
        />
      </FlexRow>
    </FlexCol>

    <FlexCol class="justify-content-center align-items-center">
      <p
        class="edit-mode-grey m-0 fw-bold"
        :class="{ 'is-editing': editMode && editSection === 'footer' }"
        style="
          font-size: 1rem;
          --section-height: 104px;
          --section-bottom: 0px;
          --section-top: none;
          --edit-border-radius: 0 0 24px 24px;
        "
        :style="{ color: cardSettings.textColor }"
      >
        {{ cardSettings.footer }}
      </p>
      <p
        class="m-0"
        style="font-size: 14px"
        :style="{
          color: cardSettings.textColor,
        }"
      >
        {{ cardSettings.version }}
      </p>
    </FlexCol>
  </FlexCol>
</template>

<script setup>
import FlexCol from "@/layout/FlexCol.vue";
import FlexRow from "@/layout/FlexRow.vue";
import Stamp from "./Stamp.vue";
import IconCheck from "@/assets/icons/IconCheck.vue";
import { formatDate } from "@/constants/dateUtils";
import { computed } from "vue";

const defaultCardSettings = {
  description: "BUY 8 GET 1 FREE",
  backgroundColor: "#000",
  textColor: "#fff",
  stampIcon: IconCheck,
  stampImageUrl: "",
  beforeStampsBackgroundColor: "#fff",
  beforeStampsTextColor: "#000",
  reward: "FREE CAKE",
  rewardEmoji: "",
  rewardImageUrl: "",
  footer: "EARN STAMPS WHEN YOU SPEND AT OUR STORE",
  version: `V.${formatDate(new Date(), "dd.MM.yyyy")}`,
  dateCollected: ["2024-11-10", "2024-12-10"],
  totalStamps: 8,
  rewardBackgroundColor: "#fff",
  rewardTextColor: "#000",
};

const props = defineProps({
  cardSettings: {
    type: Object,
    default: () => ({}),
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  editSection: {
    type: String,
    default: "",
  },
});

const cardSettings = computed(() => {
  return {
    ...defaultCardSettings,
    ...props.cardSettings,
  };
});

const reward = computed(() => {
  return cardSettings.value.reward.substring(0, 10).toUpperCase();
});

const checkedStamps = computed(() => cardSettings.value.dateCollected.length);
const totalStamps = computed(() => cardSettings.value.totalStamps);

const stampRows = computed(() => {
  const rows = [];
  const { totalStamps } = cardSettings.value;

  // Maximum number of stamps per row
  const maxPerRow = 5;

  // Calculate the number of rows needed
  const totalRows = Math.ceil(totalStamps / maxPerRow);
  for (let row = 0; row < totalRows; row++) {
    const columns = [];

    /**
     * Calculate the number of stamps in the current row
     * If the total number of stamps is less than 9, adjust the column size
     * to fit the stamps better
     */
    let columnSize = maxPerRow;
    if (totalStamps < 9) {
      columnSize = Math.ceil(totalStamps / totalRows);
    }

    // Fill the current row with stamp indexes
    for (let column = 0; column < columnSize; column++) {
      // Calculate the index of the stamp in the flat array based on the row and column
      const index = row * columnSize + column;
      if (index < totalStamps) {
        columns.push(index);
      }
    }

    rows.push(columns);
  }

  return rows;
});

function formatStampDate(index) {
  if (index >= 0 && index < cardSettings.value.dateCollected.length) {
    const dateStr = cardSettings.value.dateCollected[index];
    return formatDate(new Date(dateStr), "dd.MM.yy");
  }
  return "";
}

function getRowGap(itemCount) {
  switch (itemCount) {
    case 3:
      return "5rem";
    case 4:
      return "3.5rem";
    case 5:
      return "3rem";
    default:
      return "3.5rem";
  }
}
</script>
<style scoped>
.edit-mode-grey::before {
  content: "";
  position: absolute;
  top: var(--section-top, 0);
  bottom: var(--section-bottom, none);
  left: 0;
  width: 100%;
  height: var(--section-height, 100%);
  background: rgba(0, 0, 0, 0.5);
  pointer-events: none;
  border-radius: var(--edit-border-radius, none);
  transition: all 150ms ease-in-out;
}

.edit-mode-grey.is-editing::before {
  background: rgba(0, 0, 0, 0);
}

.edit-none .edit-mode-grey::before {
  background: rgba(0, 0, 0, 0) !important;
}
</style>
